import { FunctionComponent, PropsWithChildren } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    DialogActions,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Field, Formik } from 'formik';
import { FlexContainer, SectionContainer } from '@/components/Layout/Container';
import { BoldLabel, RequiredMark } from '@/components/Layout/Textual';
import { ContingentCondition } from '@/models/conditionLink';
import { FormikTextField } from '@/components/Layout/Form/Field';
import { DatePickerField } from '@/components/Layout/Form/DatePickerField';
import { WeekdaySelectField } from '@/components/Layout/Form/WeekdaySelectField';
import * as Yup from 'yup';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetProductPersonNameConfigurationQuery } from '@/api/v2/textReplacement';
import { LoadingButton } from '@mui/lab';
import { AutocompleteField } from '@/components/Layout/Form/AutoCompleteField';
import {
    ConfigurationIdNameMap,
    ProductPersonNameConfiguration,
} from '@/models/textReplacement';
import { ProductEnvironmentField } from './ProductEnvironmentField';
import { CheckboxField } from '@/components/Layout/Form/CheckboxField';
import { useGetB2BUsersQuery } from '@/api/v2/b2b';
import { FormHeader } from './ConditionList';
import {
    ConditionFormCardContent,
    Hint,
    Row,
    RowInfo,
    SectionTitle,
    TextInputContainer,
    TitleCardContent,
} from './ConditionFormComponents';

type B2BAutoCompleteValue = {
    label: string;
    id: string;
};

const ContingentFormSchema = Yup.object().shape({
    name: Yup.string().required(
        'Bitte geben Sie einen Namen für die Regel ein'
    ),
    maxTicketsPerStartDay: Yup.number()
        .required('Bitte geben Sie eine Anzahl an Ticket an')
        .min(0, 'Anzahl an Tickets muss mindestens 0 sein'),
});

const getSplitId = (id: string) => {
    if (id.length < 9) return id;

    return `${id.substring(0, 4)} ... ${id.slice(-4)}`;
};

type ContingentConditionFormProps = {
    condition: ContingentCondition;
    onSave: (condition: ContingentCondition) => void;
    onClose: () => void;
    isNewCondition: boolean;
    isLoading?: boolean;
};

export const ContingentConditionForm: FunctionComponent<
    PropsWithChildren<ContingentConditionFormProps>
> = ({ condition, onClose, onSave, isLoading, isNewCondition }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: b2bUsers } = useGetB2BUsersQuery();
    const { data } = useGetProductPersonNameConfigurationQuery(liftOperatorId);

    return (
        <Box
            sx={{
                width: '860px',
            }}
        >
            <FormHeader
                title={
                    !isNewCondition
                        ? `Regel "${condition.name}" bearbeiten`
                        : 'Neue Regel anlegen'
                }
                subtitle={
                    <>
                        Alle Felder sind optional befüllbar (Ausnahme: „Interne
                        Bezeichnung“).
                        <br />
                        Nur befüllte Felder kommen zur Anwendung und übersteuern
                        die Basiskonfiguration des Tickets, leere Felder werden
                        ignoriert.
                        <br />
                        Es kommt jeweils nur die erste Regel aus jeder Kategorie
                        („Verfügbarkeit“ / „Preis“) zur Anwendung, die auf die
                        Suchanfrage des Gastes passt.
                        <br />
                        Die Abfrage der Regeln erfolgt nach der
                        Speicheranordnung (Priorität), von oben nach unten.
                    </>
                }
            />
            <Formik
                validationSchema={ContingentFormSchema}
                validateOnChange={false}
                initialValues={condition}
                onSubmit={(values: ContingentCondition) => {
                    // ensure if values are empty that they are null
                    onSave({
                        ...values,
                        sellingGroup: values.sellingGroup || null,
                        notSellingGroup: values.notSellingGroup || null,
                        userIds: values.userIds || [],
                    });
                }}
            >
                {({ errors, values, setFieldValue, submitForm }) => (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitForm();
                        }}
                    >
                        <Stack
                            sx={{
                                p: 6,
                            }}
                            spacing={6}
                        >
                            <Card>
                                <TitleCardContent>
                                    <Typography variant='h6'>
                                        1. Regel
                                    </Typography>
                                </TitleCardContent>

                                <Divider />
                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <TextInputContainer>
                                            <FormikTextField
                                                name='name'
                                                textFieldProps={{
                                                    error: !!errors['name'],
                                                    helperText:
                                                        errors['name'] || '',
                                                    label: (
                                                        <span>
                                                            Interne Bezeichnung{' '}
                                                            <RequiredMark />
                                                        </span>
                                                    ),
                                                }}
                                            />
                                        </TextInputContainer>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.de'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName'
                                                        ]?.['de'],
                                                        helperText:
                                                            errors[
                                                                'externalName'
                                                            ]?.['de'] || '',
                                                        label: 'Anzeigename Deutsch',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.en'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName.en'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'externalName.en'
                                                            ] || '',
                                                        label: 'Anzeigename English',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        2. Bedingung&nbsp;
                                    </Typography>
                                    <Typography>
                                        - wann kommt die Regel zur Anwendung
                                    </Typography>
                                </TitleCardContent>

                                <Divider />

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Für Verkäufe bei B2B/hospitality:
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='sellingGroup'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'sellingGroup'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'sellingGroup'
                                                            ] || '',
                                                        label: 'hospitality-Verkaufsgruppe einschließen',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='notSellingGroup'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'notSellingGroup'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'notSellingGroup'
                                                            ] || '',

                                                        label: 'hospitality-Verkaufsgruppe ausschließen',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                        <Row>
                                            <TextInputContainer>
                                                <Field name='userIds'>
                                                    {({ field }) => {
                                                        const { value } = field;
                                                        const autoCompleteValues =
                                                            (value || [])
                                                                .map(
                                                                    (
                                                                        id: string
                                                                    ) =>
                                                                        b2bUsers?.b2bUsers.find(
                                                                            (
                                                                                user
                                                                            ) =>
                                                                                user.id ===
                                                                                id
                                                                        )
                                                                )
                                                                .filter(Boolean)
                                                                .map(
                                                                    (user) => ({
                                                                        label: user.name,
                                                                        id: user.id,
                                                                    })
                                                                );

                                                        return (
                                                            <Autocomplete<
                                                                B2BAutoCompleteValue,
                                                                true
                                                            >
                                                                multiple
                                                                value={
                                                                    autoCompleteValues
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    options
                                                                ) => {
                                                                    setFieldValue(
                                                                        'userIds',
                                                                        options.map(
                                                                            (
                                                                                o
                                                                            ) =>
                                                                                o.id
                                                                        )
                                                                    );
                                                                }}
                                                                disablePortal
                                                                options={
                                                                    b2bUsers?.b2bUsers.map(
                                                                        (
                                                                            user
                                                                        ) => ({
                                                                            label: user.name,
                                                                            id: user.id,
                                                                        })
                                                                    ) || []
                                                                }
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label='B2B - User auswählen'
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value
                                                                ) =>
                                                                    option.id ===
                                                                    value.id
                                                                }
                                                                renderOption={(
                                                                    props,
                                                                    option
                                                                ) => {
                                                                    const {
                                                                        ...optionProps
                                                                    } = props;
                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                option.id
                                                                            }
                                                                            sx={{
                                                                                borderRadius:
                                                                                    '8px',
                                                                                margin: '5px',
                                                                            }}
                                                                            component='li'
                                                                            {...optionProps}
                                                                        >
                                                                            <Box>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        mb: 2,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        option.label
                                                                                    }
                                                                                </Typography>

                                                                                <Typography
                                                                                    sx={{
                                                                                        ml: 1,
                                                                                    }}
                                                                                    variant='subtitle1'
                                                                                >
                                                                                    {getSplitId(
                                                                                        option.id
                                                                                    )}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                </Field>
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Kontingent Zählung
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Ist diese Checkbox aktiviert = die
                                            Kontingentierung zählt für jedes
                                            hinterlegte Produkt separat (auf
                                            Ticketebene).
                                            <br />
                                            Ist diese Checkbox deaktiviert = die
                                            Kontingentierung zählt für alle
                                            hinterlegten Produkte gemeinsam (auf
                                            Ticketebene).
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <CheckboxField
                                                    name={
                                                        'contingentsProductIdSpecific'
                                                    }
                                                    label={
                                                        'Produktspezifische Zählung'
                                                    }
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket-Gültigkeit (Startdatum)
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Hinweis für{' '}
                                            {renderSeasonTicketsBoldLabel(':')}{' '}
                                            Konfigurationen der Bedingung
                                            „Ticket-Gültigkeit“ werden erst am
                                            hinterlegten Datum im Onlineverkauf
                                            sichtbar (gleich wie bei
                                            „Ticket-Verkaufszeitraum“), da für
                                            diese Tickets im Shop kein
                                            Startdatum auswählbar ist.
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='minDaysBeforeTicketStart'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Tage vor dem Ticket-Startdatum',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row alignItems='center'>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidFrom'
                                                    label='Gültig ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidTo'
                                                    label='Gültig bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row>
                                            <Stack>
                                                <Typography
                                                    sx={{
                                                        mb: 2,
                                                    }}
                                                >
                                                    In diesem Zeitraum nur
                                                    gültig an den Wochentagen:
                                                </Typography>
                                                <WeekdaySelectField
                                                    name={
                                                        'startDateValidWeekdays'
                                                    }
                                                />
                                            </Stack>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket Verkaufszeitraum&nbsp;
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wenn der Verkaufszeitraum in der
                                            Zukunft liegt, ist die Regel auch
                                            erst im hinterlegten
                                            Verkaufszeitraum im Shop sichtbar!
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validFrom'}
                                                    label='Verkaufszeitraum ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validTo'}
                                                    label='Verkaufszeitraum bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Stack>
                                            <RowInfo>
                                                In diesem Zeitraum nur gültig an
                                                den Wochentagen:
                                            </RowInfo>
                                            <WeekdaySelectField
                                                name={'validWeekdays'}
                                            />
                                        </Stack>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography variant={'subtitle2'}>
                                        Für folgende Personentypen
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wähle hier all jene
                                            Personenkategorien, für welche die
                                            Regel zur Anwendung kommen soll.
                                            Hier gelistet sind die
                                            Originalbeschreibungen, wie sie von
                                            SKIDATA an starjack übermittelt
                                            werden:
                                        </Hint>

                                        <Row>
                                            <AutocompleteField
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiAutocomplete-tag': {
                                                        mx: 1,
                                                        my: 2,
                                                    },
                                                }}
                                                name='personTypeIds'
                                                options={data?.persons || []}
                                                getOptionLabel={(
                                                    option: ConfigurationIdNameMap
                                                ) => option.name}
                                                onChange={(options) => {
                                                    setFieldValue(
                                                        'personTypeIds',
                                                        options.map((o) => o.id)
                                                    );
                                                }}
                                                valueTransformHandler={(
                                                    values: string[]
                                                ) => {
                                                    if (data) {
                                                        const { persons } =
                                                            data as ProductPersonNameConfiguration;
                                                        return persons.filter(
                                                            (p) =>
                                                                values.includes(
                                                                    p.id
                                                                )
                                                        );
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label='Personentypen auswählen'
                                                    />
                                                )}
                                                renderTags={(
                                                    value: ConfigurationIdNameMap[],
                                                    getTagProps
                                                ) =>
                                                    value.map(
                                                        (
                                                            option: ConfigurationIdNameMap,
                                                            index: number
                                                        ) => (
                                                            <Chip
                                                                key={index}
                                                                color='primary'
                                                                label={
                                                                    option.name
                                                                }
                                                                {...getTagProps(
                                                                    {
                                                                        index,
                                                                    }
                                                                )}
                                                            />
                                                        )
                                                    )
                                                }
                                            />
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography variant={'subtitle2'}>
                                        Für folgende Umgebungen
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack>
                                        <ProductEnvironmentField
                                            values={values['environments']}
                                            onChange={(environments) => {
                                                setFieldValue(
                                                    'environments',
                                                    environments
                                                );
                                            }}
                                        />
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        3. Aktion&nbsp;
                                    </Typography>
                                    <Typography>- daraus resultiert</Typography>
                                </TitleCardContent>
                                <Divider />

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='maxTicketsPerStartDay'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: (
                                                            <span>
                                                                Anzahl an
                                                                Tickets (pro
                                                                Tag)
                                                                <RequiredMark />
                                                            </span>
                                                        ),
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='maxTicketsForDateRange'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Gesamt-Kontingent für Zeitraum',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row alignItems='center'>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='contingentRangeFrom'
                                                    label='Kontingent-Zeitraum von'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='contingentRangeTo'
                                                    label='Kontingent-Zeitraum bis'
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            {!!Object.keys(errors).length && (
                                <SectionContainer>
                                    <FlexContainer>
                                        <Typography>
                                            Bitte füllen Sie zuerst alle
                                            Pflichtfelder aus
                                        </Typography>
                                    </FlexContainer>
                                </SectionContainer>
                            )}

                            <DialogActions>
                                <Button onClick={onClose} color='primary'>
                                    Schließen
                                </Button>

                                <LoadingButton
                                    type='submit'
                                    color='primary'
                                    variant='contained'
                                    loading={isLoading}
                                >
                                    Speichern
                                </LoadingButton>
                            </DialogActions>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

const renderSeasonTicketsBoldLabel = (addOn = '') => (
    <BoldLabel
        sx={{
            fontSize: '0.85rem',
        }}
    >
        Saison-/Jahres- oder Depotkarten{addOn}
    </BoldLabel>
);
